import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';


import FlightStatusCard   from '../components/FlightStatusCard';
import LocationCard       from '../components/LocationCard';
import WeatherCard        from '../components/WeatherCard';
import TFRCard            from '../components/TFRCard';
import AirspaceCard       from '../components/AirspaceCard';
import ADSBAircraftCard   from '../components/ADSBAircraftCard';
// import LightningCard    from '../components/LightningCard';
// import NotesCard        from '../components/NotesCard';
import PlaceholderCard    from '../components/PlaceholderCard';
import NearbyHazardsCard  from '../components/NearbyHazardsCard';

const SEVERITY_RANK = { 'no-go': 4, 'caution': 3, 'advisory': 2, 'go': 1, 'unknown': 0 };

function getFlightStatus(statuses) {
  let worst = 'unknown';
  for (const sev of Object.values(statuses)) {
    if (SEVERITY_RANK[sev] > SEVERITY_RANK[worst]) worst = sev;
  }
  let message = '';
  switch (worst) {
    case 'no-go':    message = 'Do not fly'; break;
    case 'caution':  message = 'Flight not recommended'; break;
    case 'advisory': message = 'Proceed with advisories'; break;
    case 'go':       message = 'Clear for takeoff'; break;
  }
  return { level: worst, message };
}



  
function pickWorstKey(statuses) {
  let worstKey = null;
  let worst    = 'unknown';
  Object.entries(statuses).forEach(([k, sev]) => {
    if (SEVERITY_RANK[sev] > SEVERITY_RANK[worst]) {
      worst    = sev;
      worstKey = k;
    }
  });
  return worstKey;
}

const VALID_SEV = new Set(['go','advisory','caution','no-go','unknown']);
const norm      = s => (VALID_SEV.has(s) ? s : 'unknown');
const hasSearch = (lat, lon) => lat != null && lon != null;

const REQUIRED = ['weather', 'tfr', 'airspace', 'NearbyHazards'];

export default function Home({ userLat, userLon, selectedDrone }) {
  
    const [open, setOpen] = useState(true);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
	  };
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    const hasVisited = localStorage.getItem("hasVisited");
    if (!hasVisited) setShowWelcome(true);
  }, []);
  const handleAcknowledge = () => {
    localStorage.setItem("hasVisited", "true");
    setShowWelcome(false);
  };

  const [refreshCounter, setRefreshCounter] = useState(0);
  const searchStarted = hasSearch(userLat, userLon);
  const [adsbTick, setAdsbTick] = useState(0);
  const [cardStatuses, setCardStatuses] = useState({
    weather: 'unknown',
    tfr:     'unknown',
    airspace:'unknown',
    NearbyHazards:'unknown',
    kp:      'unknown',
    adsb:    'unknown',
  });
  const [cardReasons, setCardReasons] = useState({});
  const [lastUpdatedFor, setLastUpdatedFor] = useState({
    weather: null,
    tfr:     null,
    airspace:null,
    NearbyHazards:null,
    kp:      null,
    adsb:    null,
  });
  const [adsbPulse, setAdsbPulse] = useState(false);

  useEffect(() => {
    if (!searchStarted) return;
    const interval = setInterval(() => setAdsbTick(t => t + 1), 3000);
    return () => clearInterval(interval);
  }, [searchStarted]);

  useEffect(() => {
    if (searchStarted) setRefreshCounter(c => c + 1);
  }, [userLat, userLon]);

  useEffect(() => {
    if (lastUpdatedFor.adsb === refreshCounter && searchStarted) {
      setAdsbPulse(true);
      const t = setTimeout(() => setAdsbPulse(false), 450);
      return () => clearTimeout(t);
    }
  }, [lastUpdatedFor.adsb, refreshCounter, searchStarted]);

  const handleCardUpdate = (key, severity, reasons, extras = {}) => {
    if (!searchStarted) return;
    setCardStatuses(p => ({ ...p, [key]: norm(severity) }));
    if (reasons) setCardReasons(p => ({ ...p, [key]: reasons }));
    setLastUpdatedFor(p => ({ ...p, [key]: refreshCounter }));
  };

  const overall      = useMemo(() => getFlightStatus(cardStatuses), [cardStatuses]);
  const worstKey     = useMemo(() => pickWorstKey(cardStatuses),     [cardStatuses]);
  const worstReason  = worstKey ? cardReasons[worstKey]?.[0] || '' : '';

  const isReadyThisCycle =
    searchStarted && REQUIRED.every(k => lastUpdatedFor[k] === refreshCounter);

  const statusForCard = isReadyThisCycle ? overall.level : 'unknown';
  const reasonForCard = isReadyThisCycle ? (worstReason || overall.message) : '';

  return (
      <div>
      {/* This button will open the dialog */}
      <Button variant="outlined" onClick={handleClickOpen}>
        NOTICE
      </Button>

      {/* The Dialog component itself */}
      <Dialog
        open={open}
        onClose={handleClose} // Allows closing by clicking the background
      >
        <DialogTitle>
          {"Welcome"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This website is currently in <strong>beta</strong>.  
            Data shown may not be 100% accurate.  
            Please always verify with official sources before flying.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* The "OK" button that closes the dialog */}
          <Button onClick={handleClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ px:2 }}>
        <Box sx={{ maxWidth:1200, mx:'auto', pt:2 }}>
          <Grid container spacing={2}>
            <Grid span={12} sm={6} lg={6}>
              <FlightStatusCard status={statusForCard} reason={reasonForCard} />
            </Grid>
            <Grid span={12} sm={6} lg={6}>
              <LocationCard lat={userLat} lon={userLon} />
            </Grid>

            <Grid span={12} sm={6} lg={3}>
              {searchStarted ? (
                <WeatherCard
                  lat={userLat}
                  lon={userLon}
                  drone={selectedDrone}
                  refreshTrigger={refreshCounter}
                  onUpdate={(sev, reasons) => handleCardUpdate('weather', sev, reasons)}
                />
              ) : (
                <PlaceholderCard title="Weather" message="Set a location to load weather." />
              )}
            </Grid>

            <Grid span={12} sm={6} lg={3}>
              {searchStarted ? (
                <TFRCard
                  lat={userLat}
                  lon={userLon}
                  refreshTrigger={refreshCounter}
                  onUpdate={(sev, reasons) => handleCardUpdate('tfr', sev, reasons)}
                />
              ) : (
                <PlaceholderCard title="Nearest TFRs" message="No location yet. Press GO." />
              )}
            </Grid>

            <Grid span={12} sm={6} lg={3}>
              {searchStarted ? (
                <AirspaceCard
                  lat={userLat}
                  lon={userLon}
                  refreshTrigger={refreshCounter}
                  onUpdate={(sev, reasons) => handleCardUpdate('airspace', sev, reasons)}
                />
              ) : (
                <PlaceholderCard title="Airspace" message="Set a location to check airspace." />
              )}
            </Grid>

            <Grid span={12} sm={6} lg={3}>
              {searchStarted ? (
                <NearbyHazardsCard
                  lat={userLat}
                  lon={userLon}
                  refreshTrigger={refreshCounter}
                  onUpdate={(sev, reasons) => handleCardUpdate('NearbyHazards', sev, reasons)}
                />
              ) : (
                <PlaceholderCard title="Nearby Hazards" message="Set a location to check for hazards." />
              )}
            </Grid>

            <Grid span={12} sm={6} lg={3}>
              <Box
                sx={{
                  borderRadius: 1.5,
                  animation: adsbPulse ? 'flashCard 450ms ease-in-out' : 'none',
                  '@keyframes flashCard': {
                    '0%': { boxShadow: '0 0 0 rgba(0,0,0,0)' },
                    '40%':{ boxShadow: '0 0 0 6px rgba(0, 180, 255, 0.35)' },
                    '100%':{ boxShadow: '0 0 0 rgba(0,0,0,0)' },
                  },
                }}
              >
                {searchStarted ? (
                  <ADSBAircraftCard
                    lat={userLat}
                    lon={userLon}
                    refreshTrigger={adsbTick}
                    onUpdate={(sev, reasons) => handleCardUpdate('adsb', sev, reasons)}
                  />
                ) : (
                  <PlaceholderCard title="Nearby Aircraft" message="Appears after location is set." />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {showWelcome && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
            <h2 className="text-xl font-bold mb-4">Welcome to FlightIQ</h2>
            <p className="mb-4">
              This website is currently in beta and under development. Data shown may
              not be 100% accurate. Please always verify with official sources before flying.
			  Check the <strong>About</strong> page for more info.
            </p>
            <button
              onClick={handleAcknowledge}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              OK, I Understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
